import { CheckCircleFilled } from '@ant-design/icons';
import { UsersIcon } from '@heroicons/react/solid';
import { Button, Modal } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';

import { ShouldRender } from '@/components/shared/basic/ShouldRender';

import { authModalControl } from '@/atoms/authModalControl';
import { user } from '@/atoms/user';
import { Author } from '@/pages/live/[slug]';
import { updateFollow } from '@/services/School';
import { User } from '@/utils/interfaces/User';

interface AuthorCardProps {
  author: Author;
  isBookLandingPage?: boolean;
  productId: string;
  productName: string;
}

const LiveAuthorCard: React.FC<AuthorCardProps> = ({
  author,
  isBookLandingPage = false,
  productId,
  productName,
}) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [, setVisible] = useRecoilState(authModalControl);
  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const screens = useBreakpoint();

  const handleFollowClick = ({
    state,
    author_id,
    author_name,
  }: {
    state: string;
    author_id: string;
    author_name: string;
  }) => {
    const eventParams: EventDetails = {
      event_name: state,
      source: 'global',
      source_type: 'author',
      source_id: author_id as string,
      source_name: author_name,
      sub_source: 'course',
      sub_source_id: productId,
      sub_source_name: productName,
      unit: 'boolean',
      value: 1,
      meta: {
        click_source: 'course_details_screen',
      },
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const handleFollow = async ({
    author_id,
    is_follower,
  }: {
    author_id: string;
    is_follower: boolean;
  }) => {
    try {
      await updateFollow({
        author_id,
        is_follower,
      });
    } catch (error) {
      console.error('Error :', error);
    }
  };

  const toggleFollow = () => {
    const newState = !isFollowing;
    handleFollowClick({
      state: newState ? 'follow' : 'unfollow',
      author_id: author?.author_zlUid as string,
      author_name: author?.user_name as string,
    });

    setIsFollowing(newState);

    handleFollow({
      author_id: author?.author_zlUid as string,
      is_follower: newState,
    });
  };

  useEffect(() => {
    if (author?.is_followed) {
      setIsFollowing(true);
    }
  }, [author?.is_followed]);

  const followButton = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className='w-full'
      transition={{ duration: 0.5, ease: 'easeInOut' }}
    >
      <Button
        size='large'
        className='bg-[#282829] border-none text-sm font-semibold w-full'
        onClick={() => {
          if (userData) {
            isFollowing ? setIsModalVisible(true) : toggleFollow();
          } else {
            setVisible((prevState: any) => ({
              ...prevState,
              login: true,
              signUp: false,
              noRedirect: true,
            }));
          }
        }}
      >
        <motion.span
          key={isFollowing ? 'unfollow' : 'follow'}
          initial={{ y: isFollowing ? -10 : 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: isFollowing ? 10 : -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className='text-white w-full'
        >
          {isFollowing ? 'Following' : 'Follow'}
        </motion.span>
      </Button>
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className='h-full bg-[#151515] rounded-xl p-5 md:p-5 lg:p-6 flex flex-col justify-between'
    >
      <div className='flex flex-col lg:flex-row justify-between h-full gap-2 lg:gap-6 items-center md:items-start  lg:min-w-[450px] xl:min-w-[550px]'>
        {/* Avatar & Buttons (lg) */}
        <div className='w-full lg:flex-shrink-0 flex gap-x-3 lg:justify-center lg:flex-col lg:w-fit'>
          <div
            className={clsx(
              isBookLandingPage
                ? 'relative w-12 h-12  lg:w-[120px] lg:h-[120px] rounded-full overflow-hidden bg-[#252525] no-select'
                : 'relative w-12 h-12 md:w-[100px] md:h-[100px] lg:w-[120px] lg:h-[120px] rounded-full overflow-hidden bg-[#252525]',
            )}
          >
            <Image
              src={
                isBookLandingPage
                  ? author.profileImage
                  : `${process.env.NEXT_PUBLIC_STUDIO_PATH}/${author.profileImage}`
              }
              alt={author.name}
              width={160}
              height={160}
              className='object-contain'
              draggable={isBookLandingPage ? false : true}
            />
          </div>

          <ShouldRender check={screens.lg}>
            <div className='flex flex-col mt-6 items-center gap-y-3'>
              <div className='w-full'>{followButton}</div>
              <Button
                size='large'
                onClick={() => router.push(`/author/${author.user_name}`)}
                className='border-[#282829] text-white bg-transparent text-sm font-semibold w-full'
              >
                View Profile
              </Button>
            </div>
          </ShouldRender>

          <ShouldRender check={!screens.lg}>
            <div>
              <div className='flex items-center gap-2'>
                <h3
                  onClick={() =>
                    author.user_name &&
                    router.push(`/author/${author.user_name}`)
                  }
                  className='text-lg font-semibold line-clamp-1 capitalize hover:text-white hover:underline cursor-pointer'
                >
                  {author.name}
                </h3>
                {author.verified && (
                  <span className='text-red-500'>
                    <CheckCircleFilled className='h-4 w-4 fill-current' />
                  </span>
                )}
              </div>
              <div
                className={clsx(
                  isBookLandingPage && 'truncate w-[100px]',
                  'text-textLightGray  text-sm mb-4 capitalize',
                )}
              >
                {author.designation}
                <ShouldRender check={author.company}>
                  , {author.company}
                </ShouldRender>
              </div>
              <ShouldRender
                check={
                  screens.md &&
                  !screens.lg &&
                  author.learners &&
                  author.learners >= 1000
                }
              >
                <div className='flex justify-start w-full'>
                  <div className='flex items-center gap-1 text-sm bg-black w-fit py-0.5 px-3 rounded-[8px] mb-4'>
                    <UsersIcon className='h-4 w-4 text-primary' />
                    <span className='py-1 text-[#3C3B3E] px-1'>|</span>
                    <span className='text-white font-[400] text-xs'>
                      {author.learners} Learners
                    </span>
                  </div>
                </div>
              </ShouldRender>
            </div>
          </ShouldRender>
        </div>

        {/* Learners badge (for sm only) */}
        <ShouldRender
          check={!screens.md && author.learners && author.learners >= 1000}
        >
          <div className='flex justify-start w-full'>
            <div className='flex items-center gap-1 text-sm bg-black w-fit py-0.5 px-3 rounded-[8px] mb-4'>
              <UsersIcon className='h-4 w-4 text-primary' />
              <span className='py-1 text-[#3C3B3E] px-1'>|</span>
              <span className='text-white font-[400] text-xs'>
                {author.learners} Learners
              </span>
            </div>
          </div>
        </ShouldRender>

        {/* Right-side content */}
        <div className='flex-1'>
          <ShouldRender check={screens.lg}>
            <div className='flex items-center gap-2'>
              <h3
                onClick={() =>
                  author.user_name && router.push(`/author/${author.user_name}`)
                }
                className='text-lg hover:text-white hover:underline cursor-pointer font-semibold uppercase'
              >
                {author.name}
              </h3>
              {author.verified && (
                <span className='text-red-500'>
                  <CheckCircleFilled className='h-4 w-4 fill-current' />
                </span>
              )}
            </div>

            <p className='text-textLightGray capitalize text-sm mb-4'>
              {author.designation}
              <ShouldRender check={author.company}>
                , {author.company}
              </ShouldRender>
            </p>
            <ShouldRender check={author.learners && author.learners >= 1000}>
              <div className='flex items-center gap-1 text-sm bg-black w-fit py-0.5 px-3 rounded-[8px] mb-4'>
                <UsersIcon className='h-4 w-4 text-primary' />
                <span className='py-1 text-[#3C3B3E] px-1'>|</span>
                <span className='text-white font-medium text-xs'>
                  {author.learners} Learners
                </span>
              </div>
            </ShouldRender>
          </ShouldRender>

          <p
            className={clsx(
              isBookLandingPage && 'line-clamp-6',
              'font-[400] text-xs md:text-sm mb-6',
            )}
          >
            {author.bio}
          </p>
        </div>

        {/* Buttons (mobile) */}
        <ShouldRender check={!screens.lg}>
          <div className='flex lg:mt-6 items-center w-full gap-3'>
            <div className='w-full'>{followButton}</div>
            <Button
              size='large'
              onClick={() => router.push(`/author/${author.user_name}`)}
              className='border-[#282829] text-white bg-transparent text-sm font-semibold w-full'
            >
              View Profile
            </Button>
          </div>
        </ShouldRender>
      </div>
      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width='400px'
        className='bg-[#212121] rounded-lg'
        closable={false}
        centered={true}
        destroyOnHidden
      >
        <div className='flex flex-col w-full'>
          <div className='py-5'>Sure want to Un-Follow {author?.name} ?</div>
          <div className='flex gap-x-1 w-full  justify-end'>
            <span>
              <Button onClick={() => setIsModalVisible(false)} type='text'>
                No
              </Button>
            </span>
            <span>
              <Button
                onClick={() => {
                  toggleFollow();
                  setIsModalVisible(false);
                }}
                type='text'
                className='text-blue-500 font-medium'
              >
                Yes
              </Button>
            </span>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
};

export default LiveAuthorCard;
